/* eslint-disable react/no-array-index-key */
import {
  useCallback, useMemo, useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { CompanyInsightData } from '../../../../../../hooks/get-company-by-id.hook';
import {
  ItemsWrapper, TIMELINE_ITEMS_GAP_PX, TimelineLabel,
} from '../timeline.styled';
import CompanyDialog from '../../../../../../components/dialogs/company-dialog/company-dialog.component';
import STRING_KEYS from '../../../../../../language/keys';
import { ExperienceItem } from '../../../../../../@types/match';
import ExperienceContainerItem from './experience-container-item/experience-container-item.component';
import { ItemsContainer } from './experience-container.styled';

const MAX_ITEMS_IN_CLOSED_STATE = 3;

// Disables to show more X items functionality. To re enable it, delete this variable and comment out the relevant comments
const isExpanded = true;

const ExperienceContainer = ({ items, viewChangeHandler }: ExperienceContainerProps): JSX.Element | null => {
  const { t: translate } = useTranslation();
  const [[minimizedHeight, fullHeight], setHeights] = useState<[number, number]>([-1, -1]);
  // const [isExpanded, setIsExpanded] = useState<boolean>(false);

  const [selectedItem, setSelectedItem] = useState<null | ExperienceItem>(null);

  const [firstRolesCompaniesCount] = useMemo(() => {
    // This function calculates how many roles to show in a closed state defined by the variable MAX_ITEMS_IN_CLOSED_STATE.
    // If a company is in the limit of the items to show and has more roles, we will cut after the company and will have more than the max items
    let rolesCount = 0;
    let showMore = false;
    let firstRolesCompanyDiversity = 0;
    let visibleRoles = 0;

    for (let i = 0; i < items.length; i++) {
      const alreadyReachedMaxBeforeIteration = rolesCount >= MAX_ITEMS_IN_CLOSED_STATE;
      rolesCount += items[i].data.length;

      // If in current iteration we reached max or more, we still display all roles and the next companies roles will be hidden
      if (rolesCount > MAX_ITEMS_IN_CLOSED_STATE && alreadyReachedMaxBeforeIteration && !showMore) {
        visibleRoles = rolesCount - items[i].data.length;
        showMore = true;
      }

      if (!showMore) {
        firstRolesCompanyDiversity++;
      }
    }

    // setIsExpanded(!showMore);

    return [rolesCount, firstRolesCompanyDiversity, visibleRoles];
  }, [items]);

  // const moreItemsToShowAmount = useMemo(() => totalRoles - visibleRolesCount, [totalRoles, visibleRolesCount]);

  const measuredRef = useCallback((node: HTMLDivElement) => {
    if (!node) {
      return null;
    }

    let childrenHeight = 0;
    if (node && node.children?.length && firstRolesCompaniesCount) {
      for (let i = 0; i < firstRolesCompaniesCount && i < node.children.length; i++) {
        childrenHeight += node.children[i].clientHeight || 0;
      }
      childrenHeight += (firstRolesCompaniesCount - 1) * TIMELINE_ITEMS_GAP_PX;
    }

    setHeights([childrenHeight, node.clientHeight]);

    return node;
  }, [firstRolesCompaniesCount]);

  const selectedCompanyData: CompanyInsightData | null = useMemo(() => (selectedItem ? {
    companyId: selectedItem?.companyId,
    companyName: selectedItem?.companyName,
    companyLogo: selectedItem?.companyLogo,
    industries: selectedItem?.industries || [],
    companyDescription: selectedItem?.companyDescription,
    companyEnrichedData: selectedItem?.companyEnrichedData || {},
  } : null), [selectedItem]);

  const closeCompanyModal = () => {
    setSelectedItem(null);
  };

  const onItemClick = (item: ExperienceItem) => {
    setSelectedItem(item);
  };

  if (!items?.length) {
    return null;
  }

  return (
    <div>
      <TimelineLabel>
        { translate(STRING_KEYS.MATCH_CARD.EXPERIENCE.LABEL) }
      </TimelineLabel>
      <ItemsContainer
        minimized={ !isExpanded }
        minimizedHeight={ minimizedHeight }
        fullHeight={ fullHeight }
      >
        <ItemsWrapper ref={ measuredRef }>
          { items.map((item, index) => (
            <ExperienceContainerItem
              key={ item.companyName + index }
              experienceItem={ item }
              clickHandler={ () => onItemClick(item) }
              viewChangeHandler={ viewChangeHandler }
              isLastItem={ items.length - 1 === index }
            />
          )) }
        </ItemsWrapper>
      </ItemsContainer>
      { !!selectedCompanyData && (
        <CompanyDialog
          isOpen
          closeHandler={ closeCompanyModal }
          companyData={ selectedCompanyData }
        />
      ) }
    </div>
  );
};

export default ExperienceContainer;
